import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Linkedin } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ParallaxImage from "@/components/ParallaxImage";
import EditorialMarquee from "@/components/EditorialMarquee";

const principles = [
  { num: "01", title: "Awareness" },
  { num: "02", title: "Action" },
  { num: "03", title: "Authenticity" },
  { num: "04", title: "Alignment" },
  { num: "05", title: "Accountability" },
  { num: "06", title: "Agility" },
];

const pathway = [
  { num: "1", title: "Foundations & Awareness", text: "Understand what is no longer working." },
  { num: "2", title: "Trust, Authenticity & Alignment", text: "Reconnect to what truly matters." },
  { num: "3", title: "Action, Structure & Accountability", text: "Build systems that reflect your values." },
  { num: "4", title: "Agility & Long-Term Growth", text: "Lead with clarity. Evolve with purpose." },
];

const whoFor = [
  "Have all the outward appearance of success, but still feel misaligned",
  "Feel pulled in too many directions and crave deeper clarity",
  "Want long-term transformation, not another short-term fix",
  "Need a system that honours structure, depth and you",
];

const BalancedLeader = () => (
  <main data-testid="balanced-leader-page">
    <PageHero
      testId="balanced-leader-hero"
      overline="The Balanced Leader Framework"
      lines={["You are not stuck.", "You have outgrown", "the way you lead."]}
      sub="You are not broken. You are not behind. You have simply been leading without a structure that reflects who you really are."
      image="/images/rx4/dave-summit.webp"
      alt="Dave with arms raised on a fell at sunset"
    />

    {/* FROM WITHIN */}
    <section className="section-shell" data-testid="leader-within-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-7">
          <p className="overline-label mb-4">Real leadership starts from within</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone leading-snug">
            You already have everything inside you to achieve all that you desire. Trust it, act on it, and watch your world change.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
            The Balanced Leader exists to help you bring that truth to life, not by forcing more pressure, but by building clarity, self-trust and a structure that finally fits. When you lead yourself well, everything else follows.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <ParallaxImage
            src="/images/rx4/group-hike.jpg"
            alt="RX4 group walking a misty valley path"
            className="aspect-[4/5] rounded-2xl"
            speed={0.12}
          />
        </Reveal>
      </div>
    </section>

    {/* 6 PRINCIPLES */}
    <section className="bg-ink-soft border-y border-white/5" data-testid="principles-section">
      <div className="section-shell">
        <Reveal className="mb-14">
          <p className="overline-label mb-4">The Framework</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone">
            The 6 Principles of a Balanced Leader
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {principles.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.06}>
              <div
                data-testid={`principle-${p.title.toLowerCase()}`}
                className="group bg-ink border border-white/5 rounded-2xl p-8 md:p-10 h-full transition-all duration-300 hover:border-brand/40"
              >
                <span className="font-mono text-sm text-brand">[ {p.num} ]</span>
                <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-bone mt-5 transition-colors duration-300 group-hover:text-brand">{p.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* WHO IT'S FOR */}
    <section className="section-shell" data-testid="who-its-for-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <p className="overline-label mb-4">Who it is for</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone leading-snug">
            You are not new to leadership.
          </h2>
          <p className="mt-6 text-base text-slate-400 leading-relaxed">
            You have experienced the highs and lows of being a leader, but now you want more than performance. You want growth that meets you where you are and guides you to where you are meant to go.
          </p>
        </Reveal>
        <div className="lg:col-span-7 bg-ink-soft border border-white/10 rounded-2xl overflow-hidden">
          {whoFor.map((w, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div data-testid={`who-for-${i}`} className="flex items-start gap-6 border-b border-white/10 last:border-b-0 px-6 md:px-10 py-6">
                <span className="font-mono text-sm text-brand shrink-0 mt-1">0{i + 1}</span>
                <p className="text-base text-slate-300">{w}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <EditorialMarquee items={["LEAD YOURSELF WELL", "EVERYTHING ELSE FOLLOWS"]} />

    {/* PATHWAY */}
    <section className="section-shell" data-testid="pathway-section">
      <Reveal className="mb-14">
        <p className="overline-label mb-4">The Balanced Leader Pathway</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone max-w-2xl">
          Four stages. Not a productivity hack.
        </h2>
        <p className="mt-4 text-slate-400 max-w-xl">A way to build rhythm, resilience and balance in a style that fits who you are.</p>
      </Reveal>
      <div className="bg-ink-soft border border-white/10 rounded-2xl overflow-hidden">
        {pathway.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.06}>
            <div
              data-testid={`pathway-stage-${p.num}`}
              className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-baseline border-b border-white/10 last:border-b-0 px-6 md:px-10 py-8 transition-colors duration-300 hover:bg-ink-lift"
            >
              <span className="md:col-span-2 font-mono text-3xl font-bold text-brand">0{p.num}</span>
              <h3 className="md:col-span-5 font-display text-xl md:text-2xl font-semibold tracking-tight text-bone transition-colors duration-300 group-hover:text-brand">{p.title}</h3>
              <p className="md:col-span-5 text-sm text-slate-500">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* COMMUNITY CTA */}
    <section className="bg-ink-soft border-t border-white/5" data-testid="leader-community-cta">
      <div className="section-shell">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="overline-label mb-4">Want to explore more?</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone leading-snug">
              Honest conversations. Reflection prompts. No hype, no pressure.
            </h2>
            <p className="mt-5 text-slate-400 max-w-xl">
              The Balanced Leader groups are your free entry point into this way of leading. Or if you feel ready, get in touch and we can talk.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 flex flex-col gap-4">
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="community-facebook-btn"
              className="group flex items-center justify-between border border-white/15 rounded-xl px-7 py-5 text-sm font-medium uppercase tracking-widest text-bone transition-colors duration-300 hover:border-brand hover:text-brand"
            >
              <span className="flex items-center gap-3"><Users size={16} /> Free Facebook Community</span>
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="https://www.linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="community-linkedin-btn"
              className="group flex items-center justify-between border border-white/15 rounded-xl px-7 py-5 text-sm font-medium uppercase tracking-widest text-bone transition-colors duration-300 hover:border-brand hover:text-brand"
            >
              <span className="flex items-center gap-3"><Linkedin size={16} /> Free LinkedIn Community</span>
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to="/contact"
              data-testid="leader-contact-btn"
              className="btn-primary group justify-between"
            >
              Get in touch
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  </main>
);

export default BalancedLeader;
